import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useApiGet } from '../utils/httpClientTs';
import { getGroupTypes, getGroupRolesByType } from '../endpoints/groups';
import {
  GroupRole,
  GroupScope,
  GroupType,
  GroupTypeRoleSelection,
} from '../types/group';
import {
  filterValidSelections,
  isValidGroupTypeRoleSelection,
} from '../utils/groupTypeRoleSelection';
import AlertItem from './AlertItem';

const SCOPES: { value: GroupScope; label: string }[] = [
  { value: 'international', label: 'International' },
  { value: 'spd_chapter', label: 'SPD Chapter' },
  { value: 'local_chapter', label: 'Local Chapter' },
];

const sortGroupTypesByName = (groups: GroupType[]): GroupType[] =>
  [...groups].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  );

const DEFAULT_EMPTY_HINT = 'Add one or more group types to build the audience.';

export interface GroupTypeRoleSelectorProps {
  open: boolean;
  onClose: () => void;
  onSave: (selections: GroupTypeRoleSelection[]) => void;
  initialValue?: GroupTypeRoleSelection[];
  title?: string;
  enableRoleSelection?: boolean;
  requireSelection?: boolean;
  emptySelectionHint?: string;
  saveButtonLabel?: string;
}

const GroupTypeRoleSelector: React.FC<GroupTypeRoleSelectorProps> = ({
  open,
  onClose,
  onSave,
  initialValue = [],
  title = 'Select group types and roles',
  enableRoleSelection = true,
  requireSelection = false,
  emptySelectionHint = DEFAULT_EMPTY_HINT,
  saveButtonLabel = 'Save selection',
}) => {
  const [selectionMap, setSelectionMap] = useState<
    Map<string, GroupTypeRoleSelection>
  >(new Map());
  const [expandedScope, setExpandedScope] = useState<GroupScope | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [groupScopeMap, setGroupScopeMap] = useState<Map<string, GroupScope>>(
    new Map()
  );

  useEffect(() => {
    if (!open) return;
    const map = new Map<string, GroupTypeRoleSelection>();
    const scopes = new Map<string, GroupScope>();
    for (const s of initialValue) {
      map.set(s.group_type_id, { ...s });
      scopes.set(s.group_type_id, s.scope);
    }
    setSelectionMap(map);
    setGroupScopeMap(scopes);
    setExpandedGroups(new Set());
    setExpandedScope(null);
  }, [open, initialValue]);

  const {
    data: internationalGroups = [],
    isLoading: loadingInternational,
    error: internationalError,
  } = useApiGet<GroupType[]>(
    ['group-types', 'international', 'role-selector'],
    () => getGroupTypes('international'),
    { enabled: open }
  );
  const {
    data: spdGroups = [],
    isLoading: loadingSpd,
    error: spdError,
  } = useApiGet<GroupType[]>(
    ['group-types', 'spd_chapter', 'role-selector'],
    () => getGroupTypes('spd_chapter'),
    { enabled: open }
  );
  const {
    data: localGroups = [],
    isLoading: loadingLocal,
    error: localError,
  } = useApiGet<GroupType[]>(
    ['group-types', 'local_chapter', 'role-selector'],
    () => getGroupTypes('local_chapter'),
    { enabled: open }
  );

  const groupsByScope = useMemo(
    () => ({
      international: internationalGroups,
      spd_chapter: spdGroups,
      local_chapter: localGroups,
    }),
    [internationalGroups, spdGroups, localGroups]
  );

  const availableGroupsByScope = useMemo(() => {
    const selectedIds = new Set(selectionMap.keys());
    const available = (groups: GroupType[]) =>
      sortGroupTypesByName(groups.filter((gt) => !selectedIds.has(gt.id)));
    return {
      international: available(internationalGroups),
      spd_chapter: available(spdGroups),
      local_chapter: available(localGroups),
    };
  }, [internationalGroups, spdGroups, localGroups, selectionMap]);

  const handleToggleScope = (scope: GroupScope) => {
    setExpandedScope((prev) => (prev === scope ? null : scope));
  };

  const handleGroupTypeSelect = (groupType: GroupType, scope: GroupScope) => {
    const next = new Map(selectionMap);
    next.set(groupType.id, {
      scope,
      group_type_id: groupType.id,
      all_roles: true,
      role_ids: enableRoleSelection ? [] : undefined,
    });
    setSelectionMap(next);
    setGroupScopeMap((prev) => new Map(prev).set(groupType.id, scope));
    if (enableRoleSelection) {
      setExpandedGroups((prev) => new Set(prev).add(groupType.id));
    }
  };

  const handleRemoveGroup = (groupTypeId: string) => {
    const next = new Map(selectionMap);
    next.delete(groupTypeId);
    setSelectionMap(next);
    setExpandedGroups((prev) => {
      const s = new Set(prev);
      s.delete(groupTypeId);
      return s;
    });
  };

  const handleToggleExpand = (groupTypeId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupTypeId)) next.delete(groupTypeId);
      else next.add(groupTypeId);
      return next;
    });
  };

  const handleSelectAllRoles = (groupTypeId: string, checked: boolean) => {
    const current = selectionMap.get(groupTypeId);
    if (!current) return;
    const next = new Map(selectionMap);
    next.set(groupTypeId, {
      ...current,
      all_roles: checked,
      role_ids: checked ? [] : [],
    });
    setSelectionMap(next);
  };

  const handleRoleToggle = (groupTypeId: string, roleId: string) => {
    const current = selectionMap.get(groupTypeId);
    if (!current) return;
    const currentRoleIds = current.role_ids || [];
    const newRoleIds = currentRoleIds.includes(roleId)
      ? currentRoleIds.filter((id) => id !== roleId)
      : [...currentRoleIds, roleId];
    const next = new Map(selectionMap);
    next.set(groupTypeId, {
      ...current,
      all_roles: false,
      role_ids: newRoleIds,
    });
    setSelectionMap(next);
  };

  const getGroupTypeName = (groupTypeId: string): string => {
    const all = [...internationalGroups, ...spdGroups, ...localGroups];
    return all.find((gt) => gt.id === groupTypeId)?.name || groupTypeId;
  };

  const getGroupScopeLabel = (groupTypeId: string): string => {
    const scope = groupScopeMap.get(groupTypeId);
    if (!scope) return '';
    return SCOPES.find((s) => s.value === scope)?.label || scope;
  };

  const selectedEntries = useMemo(() => {
    const all = [...internationalGroups, ...spdGroups, ...localGroups];
    const nameOf = (id: string) => all.find((g) => g.id === id)?.name || id;
    return Array.from(selectionMap.entries()).sort(([a], [b]) =>
      nameOf(a).localeCompare(nameOf(b), undefined, { sensitivity: 'base' })
    );
  }, [selectionMap, internationalGroups, spdGroups, localGroups]);

  const hasInvalidRoleSelection = useMemo(() => {
    if (!enableRoleSelection) return false;
    return Array.from(selectionMap.values()).some(
      (sel) => !isValidGroupTypeRoleSelection(sel)
    );
  }, [selectionMap, enableRoleSelection]);

  const handleSave = () => {
    const values = Array.from(selectionMap.values()).map((sel) =>
      enableRoleSelection
        ? sel
        : { ...sel, all_roles: true, role_ids: undefined }
    );
    const validValues = enableRoleSelection
      ? filterValidSelections(values)
      : values;
    onSave(validValues);
    onClose();
  };

  const scopeLoading = (scope: GroupScope) => {
    if (scope === 'international') return loadingInternational;
    if (scope === 'spd_chapter') return loadingSpd;
    return loadingLocal;
  };

  const scopeError = (scope: GroupScope) => {
    if (scope === 'international') return internationalError;
    if (scope === 'spd_chapter') return spdError;
    return localError;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, maxHeight: 480, overflow: 'auto' }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 'bold', mb: 1 }}
              >
                Add group types
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                Expand a level to browse its group types, then use + to add.
              </Typography>
              <List disablePadding>
                {SCOPES.map((scope) => {
                  const isExpanded = expandedScope === scope.value;
                  const groups = availableGroupsByScope[scope.value];
                  const isLoading = scopeLoading(scope.value);
                  const error = scopeError(scope.value);
                  const totalInScope = groupsByScope[scope.value].length;
                  const selectedInScope = totalInScope - groups.length;

                  return (
                    <Box
                      key={scope.value}
                      sx={{
                        mb: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        overflow: 'hidden',
                      }}
                    >
                      <ListItemButton
                        onClick={() => handleToggleScope(scope.value)}
                        sx={{ py: 1 }}
                      >
                        <ListItemText
                          primary={scope.label}
                          secondary={
                            selectedInScope > 0
                              ? `${groups.length} available · ${selectedInScope} selected`
                              : `${groups.length} available`
                          }
                        />
                        <IconButton
                          size="small"
                          edge="end"
                          aria-label={
                            isExpanded
                              ? `Collapse ${scope.label}`
                              : `Expand ${scope.label}`
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleScope(scope.value);
                          }}
                        >
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </ListItemButton>
                      {isExpanded && (
                        <Box sx={{ px: 1, pb: 1, bgcolor: 'action.hover' }}>
                          {error && (
                            <AlertItem
                              severity="error"
                              description={`Failed to load ${scope.label.toLowerCase()} groups.`}
                              onClose={() => {}}
                              keepOpen
                            />
                          )}
                          {isLoading ? (
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                py: 2,
                              }}
                            >
                              <CircularProgress size={24} />
                            </Box>
                          ) : groups.length > 0 ? (
                            <List dense disablePadding>
                              {groups.map((groupType) => (
                                <ListItem
                                  key={groupType.id}
                                  disablePadding
                                  secondaryAction={
                                    <IconButton
                                      edge="end"
                                      size="small"
                                      aria-label={`Add ${groupType.name}`}
                                      onClick={() =>
                                        handleGroupTypeSelect(
                                          groupType,
                                          scope.value
                                        )
                                      }
                                      sx={{ color: '#0072CE' }}
                                    >
                                      <AddIcon />
                                    </IconButton>
                                  }
                                >
                                  <ListItemButton
                                    onClick={() =>
                                      handleGroupTypeSelect(
                                        groupType,
                                        scope.value
                                      )
                                    }
                                  >
                                    <ListItemText primary={groupType.name} />
                                  </ListItemButton>
                                </ListItem>
                              ))}
                            </List>
                          ) : (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ py: 1, px: 1 }}
                            >
                              {selectedInScope > 0
                                ? 'All group types from this level are already selected.'
                                : 'No group types found.'}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </List>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, maxHeight: 480, overflow: 'auto' }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 'bold', mb: 1 }}
              >
                Selected ({selectionMap.size})
              </Typography>
              {selectionMap.size > 0 ? (
                <List disablePadding>
                  {selectedEntries.map(([groupTypeId, selection]) => (
                    <SelectorItem
                      key={groupTypeId}
                      groupTypeId={groupTypeId}
                      groupTypeName={getGroupTypeName(groupTypeId)}
                      selection={selection}
                      expanded={expandedGroups.has(groupTypeId)}
                      enableRoleSelection={enableRoleSelection}
                      onToggleExpand={handleToggleExpand}
                      onSelectAllRoles={handleSelectAllRoles}
                      onRoleToggle={handleRoleToggle}
                      onRemove={handleRemoveGroup}
                      scopeLabel={getGroupScopeLabel(groupTypeId)}
                    />
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {emptySelectionHint}
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={
            (requireSelection && selectionMap.size === 0) ||
            hasInvalidRoleSelection
          }
        >
          {saveButtonLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface SelectorItemProps {
  groupTypeId: string;
  groupTypeName: string;
  selection: GroupTypeRoleSelection;
  expanded: boolean;
  enableRoleSelection: boolean;
  scopeLabel: string;
  onToggleExpand: (id: string) => void;
  onSelectAllRoles: (id: string, checked: boolean) => void;
  onRoleToggle: (id: string, roleId: string) => void;
  onRemove: (id: string) => void;
}

const SelectorItem: React.FC<SelectorItemProps> = ({
  groupTypeId,
  groupTypeName,
  selection,
  expanded,
  enableRoleSelection,
  scopeLabel,
  onToggleExpand,
  onSelectAllRoles,
  onRoleToggle,
  onRemove,
}) => {
  const {
    data: roles = [],
    isLoading,
    error,
  } = useApiGet<GroupRole[]>(
    ['group-roles', groupTypeId, 'role-selector'],
    () => getGroupRolesByType(groupTypeId),
    { enabled: expanded && enableRoleSelection }
  );

  const sortedRoles = useMemo(
    () =>
      [...roles].sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined)
          return a.order - b.order;
        if (a.order !== undefined) return -1;
        if (b.order !== undefined) return 1;
        return a.name.localeCompare(b.name);
      }),
    [roles]
  );

  return (
    <ListItem
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        mb: 1,
        flexDirection: 'column',
        alignItems: 'stretch',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
            flex: 1,
          }}
        >
          <Chip
            label={groupTypeName}
            color="primary"
            variant="outlined"
            size="small"
          />
          {scopeLabel && (
            <Chip
              label={scopeLabel}
              color="secondary"
              variant="outlined"
              size="small"
            />
          )}
          {enableRoleSelection &&
            (selection.all_roles ? (
              <Chip label="All roles" color="success" size="small" />
            ) : (
              selection.role_ids &&
              selection.role_ids.length > 0 && (
                <Chip
                  label={`${selection.role_ids.length} role${selection.role_ids.length > 1 ? 's' : ''}`}
                  size="small"
                />
              )
            ))}
        </Box>
        <Box>
          {enableRoleSelection && (
            <IconButton
              size="small"
              onClick={() => onToggleExpand(groupTypeId)}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
          <IconButton
            size="small"
            color="error"
            onClick={() => onRemove(groupTypeId)}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      </Box>
      {expanded && enableRoleSelection && (
        <Box sx={{ mt: 1, pl: 1, width: '100%' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={selection.all_roles}
                onChange={(e) =>
                  onSelectAllRoles(groupTypeId, e.target.checked)
                }
                size="small"
              />
            }
            label="All roles"
          />
          {isLoading ? (
            <CircularProgress size={22} />
          ) : error ? (
            <AlertItem
              severity="error"
              description="Failed to load roles."
              onClose={() => {}}
              keepOpen
            />
          ) : (
            sortedRoles.map((role) => (
              <FormControlLabel
                key={role.id}
                control={
                  <Checkbox
                    checked={
                      selection.all_roles ||
                      (selection.role_ids || []).includes(role.id)
                    }
                    onChange={() => onRoleToggle(groupTypeId, role.id)}
                    disabled={selection.all_roles}
                    size="small"
                  />
                }
                label={role.name}
                sx={{ display: 'block' }}
              />
            ))
          )}
        </Box>
      )}
    </ListItem>
  );
};

export default GroupTypeRoleSelector;
